/** Persisted marker for the last update check, so the notice never adds latency to a command. */
import { mkdir, readFile, writeFile } from 'node:fs/promises'
import { join } from 'node:path'
import { configDir } from '../config.js'

export interface UpdateState {
  /** ISO timestamp of the last registry check (successful or not). */
  lastCheckAt: string
  /** Latest version seen at that check (absent when the registry was unreachable). */
  latestVersion?: string
}

const DEFAULT_MAX_AGE_MS = 24 * 60 * 60 * 1000

const statePath = () => join(configDir(), 'update-check.json')

export async function readUpdateState(): Promise<UpdateState | null> {
  try {
    return JSON.parse(await readFile(statePath(), 'utf8')) as UpdateState
  } catch {
    return null
  }
}

export async function writeUpdateState(state: UpdateState): Promise<void> {
  await mkdir(configDir(), { recursive: true, mode: 0o700 })
  await writeFile(statePath(), JSON.stringify(state, null, 2) + '\n', { mode: 0o600 })
}

/** True when there is no recent check on record and the registry should be re-queried. */
export function isStale(state: UpdateState | null, maxAgeMs = DEFAULT_MAX_AGE_MS): boolean {
  if (!state?.lastCheckAt) return true
  const checkedAt = Date.parse(state.lastCheckAt)
  return !Number.isFinite(checkedAt) || Date.now() - checkedAt > maxAgeMs
}
